'use client';

import { formatTimeAgo } from '@/lib/utils/date';
import { ImportanceStars } from './ImportanceStars';
import { Avatar } from '@mui/material';
import { cn } from '@/lib/utils';
import { useCreateChat } from '@/hooks/useCreateChat';
import { useSnackbar } from '@/contexts/SnackbarContext';
import ChatStatusModal from '../ui/ChatStatusModal';
import { useUserContextStore } from '@/stores/userContextStore';
import { useTranslation } from '@/lib/i18n';
import { useRouter } from 'next/navigation';
import { trackXAdsEvent } from '@/lib/xAds';

export interface NewsCardProps {
  newsId: string;
  newsTitle: string;
  source: string;
  sourceIconUrl?: string | null;
  sourceUrl?: string | null;
  publishTime: string | Date;
  importance?: number; // 可选的新闻重要性评分
  importanceAlign?: 'left' | 'between';
  clickable?: boolean;
  onClick?: () => void;
  questions?: string[];
}

export default function NewsCard({
  newsId,
  newsTitle,
  source,
  sourceIconUrl,
  sourceUrl,
  publishTime,
  importance,
  importanceAlign = 'left',
  clickable = false,
  onClick,
  questions,
}: NewsCardProps) {
  const isBetween = importanceAlign === 'between';
  const { createChat, isCreating } = useCreateChat();
  const router = useRouter();
  const { showError } = useSnackbar();
  const { t } = useTranslation();
  const setUserContext = useUserContextStore((state) => state.setUserContext);
  const isClickable = clickable || Boolean(onClick);
  const resolvedSourceIconUrl = sourceIconUrl?.trim() || undefined;

  const handleClick = async () => {
    if (onClick) {
      onClick();
      trackXAdsEvent('click_event_card', {
        news_id: newsId,
        news_title: newsTitle,
      });
      return;
    }

    if (!clickable) return;

    trackXAdsEvent('click_event_card', {
      news_id: newsId,
      news_title: newsTitle,
    });

    try {
      setUserContext({
        context_type: 'news',
        data: {
          news_id: newsId,
          title: newsTitle,
          source,
          source_icon_url: sourceIconUrl,
          source_url: sourceUrl,
          publish_time: publishTime,
          importance_level: importance || 0,
          recommended_questions: questions || [],
        },
      });
      let sourceId = newsId ? `news_${newsId}` : '';
      router.push(`/predic/news?news_id=${newsId}`);
      // await createChat({ message: newsTitle, source: sourceId });
    } catch (err) {
      showError(t('create chat failed'));
    }
  };

  return (
    <>
      <div
        className={cn(
          'min-w-0',
          isClickable &&
            'group cursor-pointer rounded-md outline-hidden focus-visible:ring-2 focus-visible:ring-white/30',
        )}
        onClick={isClickable ? handleClick : undefined}
        onKeyDown={(event) => {
          if (!isClickable || (event.key !== 'Enter' && event.key !== ' ')) return;
          event.preventDefault();
          handleClick();
        }}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
      >
        <div className="mb-3 flex min-w-0 items-center gap-2 text-xs text-[#B7B9BC]">
          <div className="flex items-center gap-2">
            <Avatar
              sx={{ width: '16px', height: '16px', fontSize: 12, bgcolor: 'rgba(255,255,255,.5)' }}
              variant="circular"
              src={resolvedSourceIconUrl}
              slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
            >
              {source?.slice(0, 1)?.toUpperCase()}
            </Avatar>
            <span className="truncate capitalize">{source}</span>
            <span className="shrink-0">•</span>
            <span className="shrink-0">{formatTimeAgo(publishTime)}</span>
          </div>
          <div className={isBetween ? 'ml-auto' : undefined}>
            <ImportanceStars importance={importance || 0} />
          </div>
        </div>
        <h5 className="max-w-155 text-sm text-white line-clamp-3 group-hover:text-[#b7b9bc]">
          {newsTitle}
        </h5>
      </div>
      {isCreating && <ChatStatusModal />}
    </>
  );
}
